import { Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';

/**
 * Wheel hub — sits centred on the disc. Three layers:
 * <ol>
 *   <li>Dark backdrop disc (so countdown digits + "Let's Go!" read
 *       against a high-contrast surface rather than the green/blue
 *       wedges bleeding through the ring's transparent centre)</li>
 *   <li>Hub image (the commissioned gold ring PNG)</li>
 *   <li>Text overlay — countdown digit during STAKE, or "Let's Go!"
 *       once the countdown completes / the wheel is about to spin</li>
 * </ol>
 *
 * <p>Producer brief 2026-05-15 — the countdown lives INSIDE the hub
 * (was: above the wheel) so the player's eye stays on the spin
 * surface rather than ping-ponging between the wheel and a separate
 * timer overlay.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';

export interface WheelResolveHubOptions {
  /** Diameter of the hub in canvas pixels. */
  diameter: number;
  /** Optional pre-loaded hub ring texture. When null, only the
   *  backdrop + text render — useful in dev preview fixtures that
   *  haven't loaded the wheel asset bundle yet. */
  hubTexture: Texture | null;
}

export class WheelResolveHub extends Container {
  private readonly backdrop: Graphics;
  private readonly ring: Sprite | null;
  private readonly fallbackRing: Graphics | null;
  private readonly textHost: Container;
  private readonly countdownText: Text;
  private readonly letsGoText: Text;
  private diameter: number;

  constructor(options: WheelResolveHubOptions) {
    super();
    this.diameter = options.diameter;

    this.backdrop = new Graphics();
    this.addChild(this.backdrop);

    if (options.hubTexture) {
      this.ring = new Sprite(options.hubTexture);
      this.ring.anchor.set(0.5);
      this.addChild(this.ring);
      this.fallbackRing = null;
    } else {
      this.ring = null;
      this.fallbackRing = new Graphics();
      this.addChild(this.fallbackRing);
    }

    this.textHost = new Container();
    this.addChild(this.textHost);

    this.countdownText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 18,
        fontWeight: '900',
        // Producer feedback 2026-05-17 — pink/cream over solid
        // gold hub was unreadable. Dark navy matches LET'S GO.
        fill: 0x1a0e2a,
      },
    });
    this.countdownText.anchor.set(0.5, 0.5);
    this.countdownText.visible = false;
    this.textHost.addChild(this.countdownText);

    this.letsGoText = new Text({
      text: 'LET’S\nGO!',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: 12,
        fontWeight: '900',
        // Producer feedback 2026-05-17 — hub asset is solid gold,
        // so gold text was unreadable. Dark navy matches the wedge
        // separators and the overall theme palette.
        fill: 0x1a0e2a,
        align: 'center',
        letterSpacing: 0.5,
      },
    });
    this.letsGoText.anchor.set(0.5, 0.5);
    this.letsGoText.visible = false;
    this.textHost.addChild(this.letsGoText);

    this.refreshLayout();
  }

  /** Producer brief 2026-05-16 — resize the hub geometry to the
   *  given diameter, redrawing the backdrop + ring + text sizing
   *  so internals stay proportional to the canvas rather than
   *  blown up by container scaling. */
  setDiameter(diameter: number): void {
    if (diameter === this.diameter) return;
    this.diameter = diameter;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    const diameter = this.diameter;
    const radius = diameter / 2;
    const inset = diameter * 0.07;

    this.backdrop.clear();
    this.backdrop
      .circle(0, 0, radius - inset)
      .fill({ color: 0x16113d, alpha: 1 })
      .circle(0, 0, radius - inset - 6)
      .fill({ color: 0x0b0726, alpha: 0.75 });

    if (this.ring) {
      this.ring.width = diameter;
      this.ring.height = diameter;
    }
    if (this.fallbackRing) {
      this.fallbackRing.clear();
      this.fallbackRing
        .circle(0, 0, radius)
        .stroke({ color: 0xffc940, alpha: 0.85, width: 4 });
    }

    this.countdownText.style.fontSize = Math.max(
      14,
      Math.round(diameter * 0.42),
    );
    const letsGoSize = Math.max(10, Math.round(diameter * 0.15));
    this.letsGoText.style.fontSize = letsGoSize;
    this.letsGoText.style.lineHeight = Math.round(letsGoSize * 1.05);
    // Producer feedback 2026-05-17 — "LET'S GO!" wasn't visually
    // centered. Pixi's text bounds include the ascender/descender
    // padding which is heavier above the baseline than below, so
    // anchor (0.5, 0.5) lands the bounding box centre slightly
    // above the optical centre. Nudge the text down by ~6% of
    // its size to compensate. Countdown is a single digit with
    // a symmetric glyph, no nudge needed.
    this.letsGoText.y = Math.round(letsGoSize * 0.06);
    this.countdownText.y = 0;
  }

  /** Show the countdown digit during STAKE. Pass {@code null} to
   *  hide the digit (the host scene swaps to "Let's Go!" once the
   *  countdown completes). */
  setCountdown(secondsRemaining: number | null): void {
    if (secondsRemaining === null) {
      this.countdownText.visible = false;
      return;
    }
    const display = Math.max(0, Math.ceil(secondsRemaining));
    this.countdownText.text = String(display);
    // Producer feedback 2026-05-17 — keep readable on gold hub.
    // Urgent (≤2s) tilts toward a deep crimson; default dark navy.
    this.countdownText.style.fill =
      display <= 2 ? 0x6e0a1a : 0x1a0e2a;
    this.countdownText.visible = true;
    this.letsGoText.visible = false;
  }

  /** Swap the hub into the "Let's Go!" celebratory state. Called
   *  when STAKE ends and the wheel is about to spin. */
  showLetsGo(): void {
    this.countdownText.visible = false;
    this.letsGoText.visible = true;
  }

  /** Hide both labels — used on PAYOUT / IDLE when neither beat
   *  applies. */
  clear(): void {
    this.countdownText.visible = false;
    this.letsGoText.visible = false;
  }

  destroyDisplay(): void {
    this.removeChildren();
  }
}
